import {
  GEO_MODEL_PROJECTION_EXTENSION,
  GEO_MODEL_RASTER_EXTENSIONS
} from "constants/mapLayers.constants";
import { unzip } from "fflate";

/** All file extensions the geo map importer recognises as valid. */
export const VALID_EXTENSIONS: readonly string[] = [
  ...GEO_MODEL_RASTER_EXTENSIONS,
  GEO_MODEL_PROJECTION_EXTENSION
];

function hasValidExtension(name: string): boolean {
  return VALID_EXTENSIONS.some((ext) => name.toLowerCase().endsWith(ext));
}

/**
 * Returns true for macOS AppleDouble resource-fork entries (e.g.
 * `__MACOSX/._filename.zmap`) that get included in macOS-created zips.
 * Their basenames start with `._` and must be excluded even though they
 * may carry a recognised map file extension.
 */
function isMacOsMetadataFile(name: string): boolean {
  return name.startsWith("._");
}

/** Promisify the callback-based FileSystemFileEntry.file() API. */
function fileEntryToFile(entry: FileSystemFileEntry): Promise<File> {
  return new Promise((resolve, reject) => {
    entry.file(resolve, reject);
  });
}

/**
 * Reads ALL entries from a DirectoryReader by calling readEntries() in a loop
 * until it returns an empty array (the spec mandates max 100 per call).
 */
async function readAllEntries(
  reader: FileSystemDirectoryReader
): Promise<FileSystemEntry[]> {
  const results: FileSystemEntry[] = [];
  let batch: FileSystemEntry[];
  do {
    batch = await new Promise<FileSystemEntry[]>((resolve, reject) => {
      reader.readEntries(resolve, reject);
    });
    results.push(...batch);
  } while (batch.length > 0);
  return results;
}

/**
 * Recursively traverses a FileSystemEntry (file or directory) and returns all
 * File objects found at any depth.
 */
async function traverseEntry(entry: FileSystemEntry): Promise<File[]> {
  if (entry.isFile) {
    const file = await fileEntryToFile(entry as FileSystemFileEntry);
    return [file];
  }

  if (entry.isDirectory) {
    const reader = (entry as FileSystemDirectoryEntry).createReader();
    const children = await readAllEntries(reader);
    const nested = await Promise.all(children.map(traverseEntry));
    return nested.flat();
  }

  return [];
}

/**
 * Extracts all valid map files from a .zip File using fflate.
 * Ignores any entries that are not recognised map formats.
 */
export function extractFromZip(zipFile: File): Promise<File[]> {
  return new Promise((resolve, reject) => {
    zipFile.arrayBuffer().then((buffer) => {
      const data = new Uint8Array(buffer);
      unzip(data, (err, unzipped) => {
        if (err) {
          reject(err);
          return;
        }

        const files: File[] = [];
        for (const [path, fileData] of Object.entries(unzipped)) {
          // Skip directory entries (zero-length entries ending with '/')
          if (path.endsWith("/")) continue;

          const basename = path.split("/").pop() ?? path;
          if (isMacOsMetadataFile(basename)) continue;
          if (!hasValidExtension(basename)) continue;

          // Cast required: fflate returns Uint8Array<ArrayBufferLike> which TypeScript
          // doesn't recognise as BlobPart directly, but browsers accept it at runtime.
          files.push(
            new File([fileData as unknown as BlobPart], basename, {
              type: "application/octet-stream"
            })
          );
        }
        resolve(files);
      });
    }, reject);
  });
}

/**
 * Expands a DataTransferItemList (from a drag-and-drop event) into a flat list
 * of valid map Files. Handles:
 *  - Regular files (passed through if valid extension)
 *  - Folders (recursively traversed via FileSystem Access API)
 *  - .zip archives (extracted via fflate)
 *
 * Returns only files with recognised map extensions.
 */
export async function expandDroppedItems(items: DataTransferItemList): Promise<File[]> {
  const entryPromises: Promise<File[]>[] = [];

  for (const item of Array.from(items)) {
    if (item.kind !== "file") continue;

    // Prefer FileSystem Access API which supports directories
    const entry = item.webkitGetAsEntry?.();
    if (entry) {
      entryPromises.push(traverseEntry(entry));
    } else {
      // Fallback: plain File object (no directory support)
      const file = item.getAsFile();
      if (file) entryPromises.push(Promise.resolve([file]));
    }
  }

  const allFilesNested = await Promise.all(entryPromises);
  const allFiles = allFilesNested.flat();

  // Separate zips from regular files
  const zips = allFiles.filter((f) => f.name.toLowerCase().endsWith(".zip"));
  const nonZips = allFiles.filter((f) => !f.name.toLowerCase().endsWith(".zip"));

  // Extract zip contents
  const extractedNested = await Promise.all(zips.map(extractFromZip));
  const extracted = extractedNested.flat();

  // Combine and filter to valid extensions only
  return [...nonZips, ...extracted].filter(
    (f) => hasValidExtension(f.name) && !isMacOsMetadataFile(f.name)
  );
}

/**
 * Expands a plain File[] (from a file picker) into a flat list of valid map
 * Files. Handles .zip entries by extracting them; passes through others.
 *
 * Returns only files with recognised map extensions.
 */
export async function expandFileList(files: File[]): Promise<File[]> {
  const zips = files.filter((f) => f.name.toLowerCase().endsWith(".zip"));
  const nonZips = files.filter((f) => !f.name.toLowerCase().endsWith(".zip"));

  const extractedNested = await Promise.all(zips.map(extractFromZip));
  const extracted = extractedNested.flat();

  return [...nonZips, ...extracted].filter(
    (f) => hasValidExtension(f.name) && !isMacOsMetadataFile(f.name)
  );
}
